<template>
    <form @submit.prevent="onSubmit" ref="form" action="/settings/profile" method="post" enctype="multipart/form-data" :disabled="!cardSetupComplete">
        <input type="hidden" name="signup_type" v-model="form.signup_type">
        <input v-show="form.existing_user_id" type="hidden" name="existing_user_id" v-model="form.existing_user_id">

        <div v-show="errors.length > 0" class="alert alert-bad">
            <ul>
                <li v-for="error in errors" :key="error"><span class="fa fa-flag"></span>{{ error }}</li>
            </ul>
        </div>

        <div class="general-title">
            <div class="title">Sign Up</div>
        </div>
        <div class="input-container">
            <div class="input-title">Relationship</div>
            <div class="input-box">
                <selectize placeholder="Choose Relationship" name="title" v-model="form.title">
                    <option value="PARENT">Parent</option>
                    <option value="CAREGIVER">Caregiver</option>
                    <option value="PROFESSIONAL">Professional</option>
                </selectize>
            </div>
        </div>
        <div class="input-container">
            <div class="input-title">First Name</div>
            <div class="input-box"><input type="text" name="first_name" v-model="form.first_name"></div>
        </div>
        <div class="input-container">
            <div class="input-title">Last Name</div>
            <div class="input-box"><input type="text" name="last_name" v-model="form.last_name"></div>
        </div>
        <div class="input-container">
            <div class="input-title">Email Address</div>
            <div class="input-box"><input type="text" name="email" v-model="form.email"></div>
        </div>
        <div class="input-container">
            <div class="input-title">Password</div>
            <div class="input-box"><input type="password" name="password" v-model="form.password"></div>
        </div>
        <div class="input-container">
            <div class="input-title">Confirm Password</div>
            <div class="input-box"><input type="password" name="password_confirmation" v-model="form.password_confirmation"></div>
        </div>
        <photo-upload name="avatar" title="Your Profile Photo" preview-title="Photo To Upload"></photo-upload>
        <br>
        <div class="general-title">
            <div class="title">Account Profile</div>
        </div>
        <div class="input-container">
            <div class="input-title">Account Name</div>
            <div class="input-box"><input type="text" name="account_name" :placeholder="accountPlaceholder" v-model="form.account_name"></div>
        </div>
        <photo-upload name="account_avatar" title="Account Avatar" preview-title="Avatar To Upload"></photo-upload>
        <br>
        <div v-show="isBillable">
            <div class="tool-tip add-buffer">
                <div class="left">
                    <div class="headline">Your Free Trial</div>
                    You will not be charged until {{ firstBillingDate }}, when your 90-day trial ends. You may cancel your account on any date before that.
                </div>
            </div>
            <div class="input-container">
                <div class="input-title">Select Your Country</div>
                <div class="input-box">
                    <selectize v-model="form.billing_country" name="billing_country" placeholder="Select Country" required>
                        <option v-for="country in countries" :key="country.value" :value="country.value">{{ country.name }}</option>
                    </selectize>
                </div>
            </div>
            <div v-show="form.billing_country === 'USA'" class="input-container">
                <div class="input-title">Select Your State</div>
                <div class="input-box">
                    <selectize v-model="form.billing_state" name="billing_state" placeholder="Select State" required>
                        <option v-for="state in states" :key="state.value" :value="state.value">{{ state.name }}</option>
                    </selectize>
                </div>
            </div>

            <input type="hidden" name="billing_zip" v-model="form.billing_zip">

            <card-form @completed="cardCompleted"></card-form>
        </div><br>

        <div v-show="canShowBillingInfo" class="general-title">
            <div class="title">Choose Account Type</div>
        </div>

        <div v-show="canShowBillingInfo" class="input-container">
            <div class="input-title">How Many Children?</div>
            <label class="input-radio">
                <input type="radio" name="max_children" value="4" v-model="form.max_children">
                <span class="radio-checkmark"></span>
                <div class="radio-text">Up to four children</div>
            </label>
            <label class="input-radio">
                <input type="radio" name="max_children" value="6" v-model="form.max_children">
                <span class="radio-checkmark"></span>
                <div class="radio-text">Up to six children</div>
            </label>
        </div>
        <div v-show="canShowBillingInfo" class="input-container">
            <div class="input-title">Choose Billing Rate</div>
            <label class="input-radio">
                <input type="radio" name="billing_rate" value="YEARLY" v-model="form.billing_rate">
                <span class="radio-checkmark"></span>
                <div class="radio-text">${{ yearlyPricing }} {{ calculatedTax(yearlyPricing) }} per year</div>
            </label>
            <label class="input-radio">
                <input type="radio" name="billing_rate" value="MONTHLY" v-model="form.billing_rate">
                <span class="radio-checkmark"></span>
                <div class="radio-text">${{ monthlyPricing }} {{ calculatedTax(monthlyPricing) }} per month</div>
            </label>
        </div>

        <div class="input-container">
            <div class="input-checkbox">
                <div class="checkmark-container">
                    <label class="checkbox">
                        <input type="checkbox" id="accept_marketing_emails" name="accept_marketing_emails" value="1" checked>
                        <span class="checkmark"></span>
                    </label>
                </div>
                <label for="accept_marketing_emails" class="input-checkbox-text">Keep this box checked to receive emails from ChildBe. Much of ChildBe’s value comes from the coaching emails we send to parents and caregivers.</label>
            </div>
        </div>

        <div class="input-container">
            <div class="input-checkbox">
                <div class="checkmark-container">
                    <label class="checkbox">
                        <input type="checkbox" id="agreed_disclaimer" name="agreed_to_disclaimer" value="1" required>
                        <span class="checkmark"></span>
                    </label>
                </div>
                <label for="agreed_disclaimer" class="input-checkbox-text">I have read and agreed to the <a href="/disclaimer" target="_blank">Disclaimer</a>.</label>
            </div>
            <div class="input-checkbox">
                <div class="checkmark-container">
                    <label class="checkbox">
                        <input type="checkbox" id="agreed_terms" name="agreed_to_terms_of_use" value="1" required>
                        <span class="checkmark"></span>
                    </label>
                </div>
                <label for="agreed_terms" class="input-checkbox-text">I have read and agreed to the <a href="/terms-of-use" target="_blank">Terms of Use</a>.</label>
            </div>
            <div class="input-checkbox">
                <div class="checkmark-container">
                    <label class="checkbox">
                        <input type="checkbox" id="agreed_privacy" name="agreed_to_privacy_policy" required>
                        <span class="checkmark"></span>
                    </label>
                </div>
                <label for="agreed_privacy" class="input-checkbox-text">I have read and agreed to the <a href="/privacy-policy" target="_blank">Privacy Policy</a>.</label>
            </div>
            <div class="input-checkbox">
                <div class="checkmark-container">
                    <label class="checkbox">
                        <input type="checkbox" id="agreed_hipaa" name="read_hipaa_compliance" required>
                        <span class="checkmark"></span>
                    </label>
                </div>
                <label for="agreed_hipaa" class="input-checkbox-text">I have read the <a href="/hipaa" target="_blank">HIPAA Compliance</a> document.</label>
            </div>
        </div>

        <div v-show="errors.length > 0" class="alert alert-bad">
            <ul>
                <li><span class="fa fa-flag"></span>There were problems with your form above. Please scroll up to correct them and then try submitting the form again.</li>
            </ul>
        </div>

        <button v-show="!loading" type="submit" class="submit-standard neutral-bg" :disabled="!canShowBillingInfo && false">Create Account<span class="fa fa-check"></span></button>
        <button v-show="loading" type="submit" class="submit-standard neutral-bg" disabled>Processing...</button>

    </form>
</template>

<script>

import moment from 'moment';
import axios from 'axios';
import apiErrors from '@/utils/apiErrors';
import SubscriptionMixin from '@/mixins/SubscriptionMixin';

export default {

    props: {
        type: {
            type: String,
            default: 'STANDARD',
        },
        redirectUrl: {
            type: String,
            default: '/dashboard',
        },
        userId: {
            type: Number,
            default: null,
        },
        email: {
        	type: String,
            default: '',
        },
    },

    mixins: [ SubscriptionMixin ],

    data() {
        return {
            loading: false,
            errors: [],
            form: {
            	email: this.email,
                signup_type: this.type,
                max_children: 4,
                billing_rate: 'YEARLY',
                existing_user_id: this.userId,
            },
        }
    },

    computed: {

        isBillable() {
            return this.type === 'STANDARD';
        },

        accountPlaceholder() {
            if (this.form.last_name && this.form.last_name.length > 0) {
                return 'Ex: ' + this.form.last_name + ' Family';
            }

            return 'Ex: Your Family';
        },

    },

    watch: {
        'form.billing_state': async function(newValue) {
            this.getTaxRate();
        },

        'form.billing_zip': async function(newValue) {
            this.getTaxRate();
        },
    },

    methods: {

        async onSubmit() {
            this.loading = true;
            this.errors = [];

            try {
                const { data } = await axios.post('/signup', new FormData(this.$refs.form));

                if (data.success) {
                    this.handleSuccessfulSubscription();
                }
            } catch (e) {
                if (e.response && e.response.data && e.response.data.errors) {
                    Object.values(e.response.data.errors).forEach(errors => {
                        errors.forEach(error => {
                            this.errors.push(error);
                        });
                    });

                    document.querySelector('html').scrollTop = 0;
                } else {
                    this.errors = [ apiErrors.getErrorMessage(e) ];

                    document.querySelector('html').scrollTop = 0;
                }
            }

            this.loading = false;
        },

        handleSuccessfulSubscription() {
            return window.location.href = this.redirectUrl;
        },
    }
}

</script>



// WEBPACK FOOTER //
// resources/js/components/signup/CreateAccountForm.vue